import React, { useState, Fragment } from "react";
import HoverIconButton from "components/HoverIconButton";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { STATE_KEYS } from "utils/constants/state";
import StripeExternalLink from "components/StripeExternalLink";
import Pill from "components/Pill";
import { reorderPricingLayout } from "utils/pricing";
import { LIST_DESTINATIONS, listDragProps } from "utils/drag";
import { TOOLTIP_POSITION, secondaryBackButton } from "utils/constants/ui";
import { useFormikContext } from "formik";
import get from "lodash/get";
import { productsForLayout, productsNotInLayout } from "utils/product";
import { PRICING_TABLE_PRODUCT_LIMIT_MAX } from "components/FormerEditor/common/constants";
import { getStripeDestination } from "utils/stripe";
import { PRODUCT_ORDER_INTENT } from "utils/constants/pricing";
import { usePageBuilderContext } from "utils/context";
import Analytics, {
  DRAG_PRODUCT,
  ADD_PRODUCT,
  SHOW_PRODUCT
} from "utils/analytics";
import MenuNavHeader from "components/MenuNavHeader";
import { STRIPE_ROUTES } from "utils/constants/stripe";
import SearchBar from "components/SearchBar";
import DraggableProductItem from "./DraggableProductItem";
import LimitNotice from "./LimitNotice";
import EmptyPricingTableList from "./EmptyPricingTableList";
import EmptyProductTableList from "./EmptyProductTableList";
import DragNotice from "./DragNotice";
import OffTableProductRow from "./OffTableProductRow";
import { useProductsPrices } from "utils/selectors";
import { matchSorter } from "match-sorter";

const ProductOrderSettings = ({ name }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const { tracking } = usePageBuilderContext();
  const goBack = () => {
    setFieldValue(STATE_KEYS.EDITOR.PRICING_INTENT, null);
    setFieldValue(STATE_KEYS.EDITOR.PRICING_ACTIVE_PRICE, null);
  };
  const productsPath = `${name}.data.products`;
  const layout = get(values, STATE_KEYS.MANIFEST.PRICING_LAYOUT);
  const remainderCount = PRICING_TABLE_PRODUCT_LIMIT_MAX - layout.length;
  const products = get(values, productsPath, []);
  const orderedProducts = productsForLayout(products, layout);
  const account = get(values, `${STATE_KEYS.MANIFEST.ADMIN}.account`, {});

  const { products: allProducts } = useProductsPrices();
  const allProductUIDs = allProducts.map(({ id }) => id);
  const hasProducts = allProducts.length > 0;

  const remainingProducts = productsNotInLayout(allProducts, layout);

  const [search, setSearch] = useState("");
  const filteredRemainder = matchSorter(remainingProducts, search, {
    keys: ["name"]
  });

  const productsOnTable = orderedProducts.map((product, layoutIndex) => {
    const itemProps = {
      name,
      uid: product.uid,
      title: product.title,
      collapseImages: true,
      layoutIndex,
      canEmphasize: true,
      canRemove: true,
      canAdd: false,
      dragDisabled: false,
      onClick: () => {
        Analytics.track(SHOW_PRODUCT, { ...tracking, product: product.uid });
        setFieldValue(STATE_KEYS.EDITOR.PRICING_ACTIVE_PRODUCT, product.uid);
      }
    };
    return (
      <DraggableProductItem
        key={`${product.uid}-${layoutIndex}`}
        {...itemProps}
      />
    );
  });

  const productsOffTable = filteredRemainder.map((product, layoutIndex) => {
    /**
     * Use matching product content if present
     */
    const productContentMatch = products.find(({ uid }) => uid === product.id);
    const itemProps = {
      name,
      uid: product.id,
      title: productContentMatch ? productContentMatch.title : product.name,
      images: product.images,
      layoutIndex,
      canRemove: false,
      canAdd: Boolean(remainderCount),
      disabled: true,
      dragDisabled: remainderCount === 0
    };

    return (
      <OffTableProductRow
        key={`${product.uid}-${layoutIndex}`}
        {...itemProps}
      />
    );
  });

  const hasProductsOnTable = productsOnTable.length > 0;
  const hasProductsOffTable = productsOffTable.length > 0;

  const buttonProps = secondaryBackButton({ onClick: () => goBack() });
  const CREATE_PRODUCT_COPY = "Create";

  return (
    <Fragment>
      <MenuNavHeader navSetters={[buttonProps]} label="Product order" />
      <div className="flex flex-row items-center justify-between ph3 pv2 bb hairline-1 bg-content--secondary bh--inset">
        <div className="fw5 f6 w-100">All products</div>
        <div className="pv2 flex-shrink-0">
          <StripeExternalLink
            account={account}
            copy=" "
            tooltip="View on Stripe"
            href={getStripeDestination({ path: STRIPE_ROUTES.PRODUCTS })}
          />
        </div>
      </div>
      <div className="flex flex-row justify-between items-center ph3 pv3">
        <div className="fw6 f6 w-100">{`TABLE ORDER`}</div>
        <div className="flex shrink-0 flex-row items-center">
          <Pill
            customClasses={{
              container: "flex items-end justify-end",
              copy: "f7 ph2 pv1 br2 fw5 flex-shrink-0"
            }}
            copy={`${remainderCount} remaining`}
          />
          {!remainderCount ? (
            <HoverIconButton
              onClick={() =>
                setFieldValue(STATE_KEYS.MANIFEST.PRICING_LAYOUT, [])
              }
              tooltip="Reset"
              tooltipPosition={TOOLTIP_POSITION.RIGHT}
              type="refresh"
            />
          ) : null}
        </div>
      </div>
      <DragDropContext
        onDragEnd={reorderPricingLayout({
          setFieldValue,
          setFieldTouched,
          allProductUIDs,
          layout,
          layoutPath: STATE_KEYS.MANIFEST.PRICING_LAYOUT,
          remainingProducts: filteredRemainder,
          tracking: {
            event: DRAG_PRODUCT,
            data: {
              ...tracking
            }
          }
        })}
      >
        <Droppable droppableId={LIST_DESTINATIONS.PRICING_TABLE}>
          {(provided, snapshot) => (
            <ul {...listDragProps({ provided, snapshot })}>
              {hasProductsOnTable ? (
                productsOnTable
              ) : (
                <div className="h-100 bg-white">
                  <EmptyPricingTableList
                    setFieldValue={setFieldValue}
                    hasProducts={hasProducts}
                    hasProductsOffTable={hasProductsOffTable}
                  />
                </div>
              )}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
        <div className="flex flex-row justify-between items-center ph3 pt3">
          <div className="fw6 f6">Other products</div>
          {hasProducts && (
            <div
              className="f6 fw6 link__decoration--hover color-primary pointer"
              onClick={() => {
                Analytics.track(ADD_PRODUCT, {
                  tracking,
                  copy: CREATE_PRODUCT_COPY
                });
                setFieldValue(
                  STATE_KEYS.EDITOR.PRODUCT_ORDER_INTENT,
                  PRODUCT_ORDER_INTENT.PRODUCT_ADD
                );
              }}
            >
              {CREATE_PRODUCT_COPY}
            </div>
          )}
        </div>
        {!remainderCount && (
          <div className="pt3">
            <LimitNotice />
          </div>
        )}
        <div className="w-100 relative">
          <SearchBar
            inputValue={search}
            valueSet={setSearch}
            customClasses={{
              inputWrapper:
                "flex flex-row items-center pa3 bb hairline-1 sticky top-0 z-2 bg-white bh--inset"
            }}
          />
          <Droppable droppableId={LIST_DESTINATIONS.PRODUCT_STORE}>
            {(provided, snapshot) => {
              const showDragNotice = snapshot.isUsingPlaceholder;
              return (
                <ul {...listDragProps({ provided, snapshot })}>
                  {productsOffTable.length ? (
                    productsOffTable
                  ) : (
                    <EmptyProductTableList
                      hasProducts={hasProducts}
                      search={search}
                    />
                  )}
                  {provided.placeholder}
                  {showDragNotice && <DragNotice {...snapshot} />}
                </ul>
              );
            }}
          </Droppable>
        </div>
      </DragDropContext>
    </Fragment>
  );
};

export default ProductOrderSettings;
